import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Tag } from '../../services/api';

// SVG Icons
const Icons = {
  x: (
    <svg
      className="w-4 h-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  plus: (
    <svg
      className="w-4 h-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  edit: (
    <svg
      className="w-4 h-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  trash: (
    <svg
      className="w-4 h-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  ),
};

interface TagMultiSelectProps {
  selectedTagIds: number[];
  onChange: (ids: number[]) => void;
  orgTags: Tag[];
  onTagCreated?: (tag: Tag) => void;
  onTagDeleted?: (id: number) => void;
  isManager?: boolean;
}

const COLOR_PALETTE = [
  '6366f1',
  '06b6d4',
  '10b981',
  'f59e0b',
  'ef4444',
  '8b5cf6',
  'ec4899',
  '64748b',
];

export const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
  selectedTagIds,
  onChange,
  orgTags,
  onTagCreated,
  onTagDeleted,
  isManager = false,
}) => {
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState(COLOR_PALETTE[0]);
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingColor, setEditingColor] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedTags = orgTags.filter((t) => selectedTagIds.includes(t.id));
  const unselectedTags = orgTags.filter((t) => !selectedTagIds.includes(t.id));

  const handleToggleTag = (tagId: number) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim() || !onTagCreated) return;

    const newTag: Tag = {
      id: Math.random(), // Will be replaced by server response
      name: newTagName,
      color: selectedColor,
    };

    onTagCreated(newTag);
    setNewTagName('');
    setSelectedColor(COLOR_PALETTE[0]);
  };

  const handleEditTag = (tagId: number) => {
    const tag = orgTags.find((t) => t.id === tagId);
    if (tag) {
      setEditingTagId(tagId);
      setEditingName(tag.name);
      setEditingColor(tag.color);
    }
  };

  const handleSaveEdit = async () => {
    // This would be handled by parent component's tag update handler
    setEditingTagId(null);
  };

  const handleDeleteTag = (tagId: number) => {
    if (onTagDeleted) {
      onTagDeleted(tagId);
      // Remove from selected if currently selected
      if (selectedTagIds.includes(tagId)) {
        onChange(selectedTagIds.filter((id) => id !== tagId));
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Selected Tags Display */}
      <div className={`flex flex-wrap gap-2 p-2 rounded-md border min-h-10 ${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-gray-50 border-gray-200'}`}>
        {selectedTags.map(
          (tag, idx) =>
            idx < 3 && (
              <div
                key={tag.id}
                style={{
                  backgroundColor: `#${tag.color}26`,
                  color: `#${tag.color}`,
                  borderColor: `#${tag.color}4d`,
                }}
                className="flex items-center gap-1 px-2 py-1 rounded-full border text-sm"
              >
                {tag.name}
                <button
                  onClick={() => handleToggleTag(tag.id)}
                  className="hover:opacity-70"
                >
                  {Icons.x}
                </button>
              </div>
            ),
        )}
        {selectedTags.length > 3 && (
          <div className="flex items-center text-sm text-gray-600">
            +{selectedTags.length - 3}
          </div>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className={`ml-auto transition-transform ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-gray-600 hover:text-gray-900'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute z-10 top-full left-0 right-0 mt-1 rounded-md shadow-lg ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'}`}>
          {/* Available Tags */}
          <div className={`max-h-48 overflow-y-auto ${isDark ? 'border-slate-700' : 'border-gray-200'} border-b`}>
            {unselectedTags.length > 0 ? (
              unselectedTags.map((tag) => (
                <div
                  key={tag.id}
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer group ${isDark ? 'hover:bg-slate-700 text-slate-100' : 'hover:bg-gray-100 text-gray-900'}`}
                  onClick={() => handleToggleTag(tag.id)}
                >
                  <div className="flex items-center gap-2">
                    <div
                      style={{ backgroundColor: `#${tag.color}` }}
                      className="w-3 h-3 rounded-full"
                    />
                    {tag.name}
                  </div>
                  {isManager && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleEditTag(tag.id);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        {Icons.edit}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDeleteTag(tag.id);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        {Icons.trash}
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className={`px-3 py-2 text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                No available tags
              </div>
            )}
          </div>

          {/* Manager: Create New Tag */}
          {isManager && (
            <div className={`border-t p-3 space-y-2 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <input
                type="text"
                placeholder="New tag name"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
                className={`w-full px-2 py-1 text-sm border rounded ${isDark ? 'bg-slate-900 border-slate-600 text-slate-100 placeholder-slate-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'}`}
              />
              <div className="flex gap-1">
                {COLOR_PALETTE.map((color) => (
                  <button
                    key={color}
                    style={{ backgroundColor: `#${color}` }}
                    onClick={() => setSelectedColor(color)}
                    className={`w-6 h-6 rounded-full border-2 transition-all ${
                      selectedColor === color
                        ? isDark ? 'border-slate-100' : 'border-gray-900'
                        : isDark ? 'border-slate-600' : 'border-gray-300'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={handleCreateTag}
                disabled={!newTagName.trim()}
                className={`w-full flex items-center justify-center gap-1 px-2 py-1 text-sm rounded text-white transition-colors ${isDark ? 'bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50' : 'bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50'}`}
              >
                {Icons.plus} Create
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
